import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Ban, CalendarClock, Clock, Lock, Play, Plus, Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import type { MassTarget } from "@/components/MassPanel";
import { SalesOrderMassPanel } from "@/features/sales-orders/SalesOrderMass";
import type { SalesOrderMassKind } from "@/features/sales-orders/mass";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import {
  holdsReservations,
  isOpenStatus,
  isStatusFilter,
  listSalesOrders,
  salesOrderDetailQuery,
  salesOrderListQuery,
  statusTone,
  type SalesOrderRow,
  type StatusFilter,
} from "@/features/sales-orders/api";
import { SalesOrderPanel } from "@/features/sales-orders/SalesOrderPanel";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { ImportPanel } from "@/features/import/ImportPanel";
import { salesOrderKeys } from "@/features/sales-orders/api";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { todayIso, useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";
import { salesOrderListColumns, salesOrderStatuses } from "@patronix/domain/list";
import { listView, noView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";

// Sales orders: list → detail. List = one keyset page per request through
// sales_order_page (status filter + search on number prefix / customer, with
// reserved vs open quantity per order); detail = sales_order_document (header,
// lines, reservation and availability in one request), prefetched on hover.
// Writes through the SO RPCs only.

interface Search extends ViewSearch {
  status: StatusFilter;
  q: string;
  so?: string | undefined;
  /** Open the order as a new copy (toolbar Duplicate). */
  copy?: true | undefined;
  /** The import panel is open (replaces the order detail). */
  import?: true | undefined;
}

export const Route = createFileRoute("/_app/sales/orders")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const search: Search = {
      status: isStatusFilter(raw.status) ? raw.status : "open",
      q: typeof raw.q === "string" ? raw.q.slice(0, 100) : "",
    };
    if (typeof raw.so === "string" && raw.so) search.so = raw.so;
    if (search.so && search.so !== "new" && (raw.copy === true || raw.copy === "true")) search.copy = true;
    if (raw.import === true || raw.import === "true") search.import = true;
    return { ...search, ...viewSearch(salesOrderListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q, f: search.f, s: search.s }),
  // Start the first page while the route chunk is still loading.
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(salesOrderListQuery(listParams(companyId, deps)));
  },
  component: SalesOrdersPage,
});

function listParams(companyId: string, s: Pick<Search, "status" | "q" | "f" | "s">) {
  return { companyId, status: s.status, q: s.q, ...viewQuery(salesOrderListColumns, s) };
}

const filters: StatusFilter[] = ["open", "draft", "released", "partially_shipped", "shipped", "all"];

function SalesOrdersPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canManage = useCan("sales.order.manage");
  const canImport = useCan("core.data.import") && canManage;
  const { company } = useActiveCompany();
  // Mass operation open in the detail area (docs/design/mass-operations.md).
  const [mass, setMass] = useState<{ kind: SalesOrderMassKind; target: MassTarget; count: number | null } | null>(null);
  const exportColumns: ExportColumn<SalesOrderRow>[] = [
    { header: t("so.colOrder"), kind: "code", value: (r) => r.number },
    { header: t("export.customerCode"), kind: "code", value: (r) => r.customer_code },
    { header: t("so.colCustomer"), kind: "text", value: (r) => r.customer_name },
    { header: t("so.colStatus"), kind: "code", value: (r) => r.status },
    { header: t("po.orderDate"), kind: "date", value: (r) => r.order_date },
    { header: t("so.requested"), kind: "date", value: (r) => r.next_requested_date },
    { header: t("so.colShortLines"), kind: "integer", value: (r) => (holdsReservations(r.status) ? r.short_line_count : null) },
    { header: t("po.colAmount"), kind: "number", value: (r) => r.total_amount, unit: (r) => r.currency },
    { header: t("po.currency"), kind: "code", value: (r) => r.currency },
    { header: t("export.lines"), kind: "integer", value: (r) => r.line_count },
  ];

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const listTools: ListTools<SalesOrderRow> = {
    id: "sales-orders",
    title: t("so.title"),
    onNew: canManage
      ? () => {
          setMass(null);
          setSearch({ so: "new", copy: undefined, import: undefined });
        }
      : undefined,
    onDuplicate: canManage
      ? (r) => {
          setMass(null);
          setSearch({ so: r.id, copy: true, import: undefined });
        }
      : undefined,
    export: { fileBase: "sales-orders", sheet: t("so.title"), columns: exportColumns, fetchPage: (cursor, signal) =>
              listSalesOrders(listParams(company?.id ?? "", search), cursor, signal, EXPORT_PAGE) },
    onImport: canImport
      ? () => {
          setMass(null);
          setSearch({ import: true, so: undefined });
        }
      : undefined,
    mass:
      canManage && company
        ? (
            [
              { key: "release", label: t("mass.salesOrders.release"), icon: <Play size={15} /> },
              { key: "move", label: t("mass.salesOrders.move"), icon: <CalendarClock size={15} /> },
              { key: "close", label: t("mass.salesOrders.close"), icon: <Lock size={15} /> },
              { key: "cancel", label: t("mass.salesOrders.cancel"), icon: <Ban size={15} /> },
            ] as const
          ).map((a) => ({
            ...a,
            open: (target: MassTarget, count: number | null) => {
              setSearch({ so: undefined, copy: undefined, import: undefined });
              setMass({ kind: a.key, target, count });
            },
          }))
        : [],
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.sales.title")} <span aria-hidden="true">/</span> {t("nav.sales.orders")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("so.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => {
              setMass(null);
              setSearch({ so: "new", import: undefined });
            }}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("so.newOrder")}
          </button>
        )}
      </header>

      <Toolbar search={search} setSearch={setSearch} />

      <div className="flex min-h-0 grow gap-4">
        <OrderList search={search} setSearch={setSearch} tools={listTools} onPick={() => setMass(null)} />
        <DetailPanel
          label={mass ? t(`mass.salesOrders.${mass.kind}Title`) : search.import ? t("import.title") : t("so.detailLabel")}
          open={!!mass || !!search.so || !!search.import}
          placeholder={<DetailPlaceholder title={t("po.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {mass ? (
            <SalesOrderMassPanel
              key={`${mass.kind}:${mass.target.kind}:${mass.count ?? "all"}`}
              kind={mass.kind}
              target={mass.target}
              count={mass.count}
              list={listParams(company?.id ?? "", search)}
              onClose={() => setMass(null)}
            />
          ) : search.import ? (
            <ImportPanel
              entities={["sales_orders"]}
              initial="sales_orders"
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={[salesOrderKeys.all]}
            />
          ) : search.so && (
            <SalesOrderPanel
              key={`${search.so}:${search.copy ? "copy" : ""}`}
              soId={search.so}
              startAsCopy={!!search.copy}
              onClose={() => setSearch({ so: undefined, copy: undefined })}
              onOpen={(id) => setSearch({ so: id, copy: undefined })}
            />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}

function Toolbar({ search, setSearch }: { search: Search; setSearch: (p: Partial<Search>) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(search.q);

  // Debounce typing into the URL (and therefore the query key) by 200 ms.
  // Superseded requests are cancelled through the query's AbortSignal.
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, so: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const label = (f: StatusFilter) => (f === "open" ? t("po.filterOpen") : f === "all" ? t("po.filterAll") : t(`status.${f}`));

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label={t("so.colStatus")} className="flex flex-wrap gap-2">
        {filters.map((f) => {
          const active = search.status === f;
          return (
            <button
              key={f}
              type="button"
              aria-pressed={active}
              onClick={() => setSearch({ status: f, so: undefined })}
              className={cn(
                "h-8 rounded-full border px-3 text-[13px] transition-colors",
                active ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
              )}
            >
              {label(f)}
            </button>
          );
        })}
      </div>
      <div className="grow" />
      <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
        <Search size={15} aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("so.searchPlaceholder")}
          aria-label={t("so.searchPlaceholder")}
          data-list-search
          className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
        />
        {text && (
          <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
            <X size={14} />
          </button>
        )}
      </label>
    </div>
  );
}

function OrderList({
  search,
  setSearch,
  tools,
  onPick,
}: {
  search: Search;
  setSearch: (p: Partial<Search>) => void;
  tools: ListTools<SalesOrderRow>;
  /** A row was opened: the mass panel gives way to the order. */
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const today = todayIso();
  const { company } = useActiveCompany();
  const query = useInfiniteQuery(salesOrderListQuery(listParams(company?.id ?? "", search)));

  const isLate = (r: SalesOrderRow) => isOpenStatus(r.status) && !!r.next_requested_date && r.next_requested_date < today;

  const columns: Column<SalesOrderRow>[] = [
    {
      key: "number",
      header: t("so.colOrder"),
      width: "minmax(132px,0.9fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.number}</span>,
    },
    {
      key: "date",
      header: t("po.orderDate"),
      width: "minmax(112px,0.8fr)",
      cell: (r) => <span className="text-ink-muted">{fmt.day(r.order_date)}</span>,
    },
    {
      key: "customer",
      header: t("so.colCustomer"),
      width: "minmax(150px,1.4fr)",
      cell: (r, selected) => <span className={cn("truncate", selected && "font-medium")}>{r.customer_name}</span>,
    },
    {
      key: "status",
      header: t("so.colStatus"),
      width: "minmax(140px,1fr)",
      cell: (r) => <Badge tone={statusTone[r.status]}>{t(`status.${r.status}`)}</Badge>,
      options: salesOrderStatuses.map((v) => ({ value: v, label: t(`status.${v}`) })),
    },
    {
      key: "requested",
      header: t("so.colRequested"),
      width: "minmax(112px,0.8fr)",
      cell: (r) => {
        const late = isLate(r);
        return (
          <span className={cn("flex items-center gap-1.5", late ? "font-medium text-(--status-danger-fg)" : "text-ink-muted")}>
            {r.next_requested_date ? fmt.day(r.next_requested_date) : "—"}
            {late && <Clock size={13} strokeWidth={2.2} aria-label={t("po.late")} role="img" />}
          </span>
        );
      },
    },
    {
      key: "reserved",
      header: t("so.colReserved"),
      width: "minmax(120px,0.8fr)",
      align: "end",
      cell: (r) => {
        if (!holdsReservations(r.status)) return <span className="text-ink-muted">—</span>;
        return r.short_line_count === 0 ? (
          <span className="text-(--status-success-fg)">{t("so.fullyReserved")}</span>
        ) : (
          <span className="tabular font-medium text-(--status-warning-fg)">{t("so.shortCount", { count: r.short_line_count })}</span>
        );
      },
    },
    {
      key: "amount",
      header: t("po.colAmount"),
      width: "minmax(140px,1fr)",
      align: "end",
      cell: (r) => <span className="tabular">{fmt.money(String(r.total_amount), r.currency)}</span>,
    },
  ];

  return (
    <DataList
      label={t("so.listLabel")}
      columns={columns}
      query={query}
      rowId={(r) => r.id}
      selectedId={search.so}
      onSelect={(r) => {
        onPick();
        setSearch({ so: r.id, copy: undefined });
      }}
      onClear={() => setSearch({ so: undefined })}
      onPrefetch={(r) => void queryClient.prefetchQuery(salesOrderDetailQuery(r.id))}
      emptyTitle={t("so.emptyTitle")}
      emptyBody={t("po.emptyBody")}
      emptyAction={
        <button
          type="button"
          onClick={() => setSearch({ status: "open", q: "", so: undefined, ...noView })}
          className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
        >
          {t("list.clearFilters")}
        </button>
      }
      minWidth={920}
      tools={tools}
      view={listView(salesOrderListColumns, search, setSearch)}
    />
  );
}
